import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import isIBAN from 'validator/lib/isIBAN';
import { Account, AccountIdentifier, AccountStatus } from '~/types/account';
import { ConsentStatus } from '~/types/consent';
import { isDefined } from '~/types/typeGuards';
import { isConsentAuthorised, isConsentDisabled } from './consents';
import { hasBulkPaymentsFlag, hasInstantPaymentsFlag, hasPaymentsFlag } from './institutions';

export const resolveAccountIdentifier = (account: Account): AccountIdentifier | null | undefined => {
  if (isEmpty(account.identifiers)) return null;

  const { iban, accountNumber, email } = account.identifiers.reduce<{
    iban?: AccountIdentifier;
    accountNumber?: AccountIdentifier;
    email?: AccountIdentifier;
  }>((acc, val) => {
    if (val.type === 'IBAN') {
      acc.iban = val;
    } else if (val.type === 'BIC' && isIBAN(val.value)) {
      acc.iban = val;
    } else if (val.type === 'ACCOUNT_NUMBER') {
      acc.accountNumber = val;
    } else if (val.type === 'EMAIL') {
      acc.email = val;
    }

    return acc;
  }, {});

  if (iban) return iban;
  if (accountNumber) return accountNumber;
  if (email) return email;
  return first(account.identifiers);
};

export const shortensAccountIdentifier = (identifier: AccountIdentifier | null | undefined) => {
  if (!identifier) return null;
  const { type, value } = identifier;

  if (type === 'EMAIL') return value;
  if (value.length <= 5) return value;
  if (new RegExp(/^[a-zA-Z\s]+$/).test(value)) return value; // IBAN THAT IS NOT IBAN 🔫
  return `***${value.substring(value.length - 5)}`;
};

export const normalizeIban = (iban: string) => iban.replace(/\s/g, '').toUpperCase();

export const findAccountByIban = (
  accounts: Account[] | undefined,
  iban: string | null | undefined,
): Account | undefined => {
  if (!accounts || !iban) return undefined;
  const target = normalizeIban(iban);
  return accounts.find((account) =>
    account.identifiers.some((id) => id.type === 'IBAN' && normalizeIban(id.value) === target),
  );
};

export const formatAccountDisplayName = (account: Account) => {
  const identifier = resolveAccountIdentifier(account);
  const shortIdentifier = shortensAccountIdentifier(identifier);
  return `${[account.nickname ?? account.consent.institution?.name, shortIdentifier]
    .filter((el) => Boolean(el))
    .join(' | ')} (${account.currency})`;
};

export const shortAccountDisplayName = (account: Account): string => {
  const identifier = resolveAccountIdentifier(account);
  const shortIdentifier = shortensAccountIdentifier(identifier);
  return [account.nickname ?? account.consent.institution?.name, shortIdentifier]
    .filter((el) => Boolean(el))
    .join(' | ');
};

export const formatAccountIdentifierWithCurrency = (account: Account) => {
  const identifier = resolveAccountIdentifier(account);
  const shortIdentifier = shortensAccountIdentifier(identifier);

  if (!shortIdentifier) return null;
  return `${shortIdentifier} (${account.currency})`;
};

/// Filter function over accounts. It checks that the given account is a manual account
export const isManualBankAccount = <T extends Account>(account: T) => {
  return !isDefined(account.consent.institution);
};

export const isLinkableBankAccount = (account: Account) =>
  isDefined(account.consent.institution) &&
  isConsentAuthorised(account.consent) &&
  account.status === AccountStatus.ACTIVE &&
  account.hiddenAt === null;

export const isEligibleForPayment = (account: Account) => {
  return isDefined(account.consent.institution) && !isConsentDisabled(account.consent) && account.hiddenAt === null;
};

export const isEligibleForF24Payment = (account: Account) => {
  return (
    isEligibleForPayment(account) &&
    account.status === AccountStatus.ACTIVE &&
    account.consent.status !== ConsentStatus.Pending &&
    account.consent.status !== ConsentStatus.Waiting
  );
};

export const hasPaymentBankAccounts = (accounts: Account[] | undefined) =>
  accounts?.some((account) => isDefined(account.consent.institution) && hasPaymentsFlag(account.consent.institution));

export const hasInstantPaymentBankAccounts = (accounts: Account[] | undefined) =>
  accounts?.some(
    (account) => isDefined(account.consent.institution) && hasInstantPaymentsFlag(account.consent.institution),
  );

export const hasBulkPaymentsBankAccount = (account: Account) =>
  isEligibleForPayment(account) &&
  isDefined(account.consent.institution) &&
  hasBulkPaymentsFlag(account.consent.institution);

/**
 * Gets the value of a specific account identifier type from an account
 * @param account - The account to search for the identifier
 * @param identifierType - The type of identifier to look for
 * @returns The value of the identifier if found, null otherwise
 * @example
 * getAccountIdentifierValue(account, 'IBAN') // 'IT60X0542811101000000123456'
 */
/**
 * Extracts the ABI code from an Italian IBAN.
 * Returns null if the IBAN is not a valid Italian IBAN format.
 * ABI code is positions 5-9, with leading zeros trimmed.
 */
export const getAbiCodeFromIban = (iban: string): string | null => {
  if (!iban.startsWith('IT') || iban.length !== 27) return null;
  return iban.slice(5, 10).replace(/^0+/, '');
};

export const getAccountIdentifierValue = (
  account: Account,
  identifierType: AccountIdentifier['type'],
): string | null => {
  if (isEmpty(account.identifiers)) return null;
  return account.identifiers.find((id) => id.type === identifierType)?.value ?? null;
};
